import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from 'lib/utils'
import * as React from 'react'

// Status variants use an 8% wash of their --status-* token for the
// surface and a 25% hairline. Info uses brand blue with theme-aware icon color.
// Status variants use their token at full strength on the
// icon, and neutral body text. Two things this replaces:
//
//   1. `border-0` in the base, which meant every `border-*` colour below was
//      dead code — the kit builds structure from hairlines, so the border is
//      now real and carries the tint.
//   2. `dark:bg-warning` / `dark:bg-success`, which resolved to the SOLID
//      status colour in dark (--warning is a pale cream in light but a burnt
//      orange in dark, since the pair inverts) — a full-strength orange slab
//      across the page. A percentage of --status-* reads as a wash on both
//      grounds because those tokens are vivid in both, so no dark: override is
//      needed at all.
//
// Only `destructive` keeps coloured body text, matching StatusIndicator: a
// failure should read as one at a glance; a warning need not shout.
const alertVariants = cva('relative w-full rounded-lg border p-3 flex gap-2 [&>svg]:shrink-0', {
  variants: {
    variant: {
      default:
        'border-st2 bg-bg2 text-primary [&>svg]:text-secondary [&>[data-slot=alert-icon]]:text-secondary',
      secondary:
        'border-st2 bg-bg1 text-primary [&>svg]:text-secondary [&>[data-slot=alert-icon]]:text-secondary',
      info: 'border-brand-text/25 bg-brand/[0.06] dark:bg-brand/[0.12] text-primary [&>svg]:text-brand-text [&>[data-slot=alert-icon]]:text-brand-text',
      warning:
        'border-status-warning/25 bg-status-warning/[0.08] text-primary [&>svg]:text-status-warning [&>[data-slot=alert-icon]]:text-status-warning',
      success:
        'border-status-success/25 bg-status-success/[0.08] text-primary [&>svg]:text-status-success [&>[data-slot=alert-icon]]:text-status-success',
      destructive:
        'border-status-danger/25 bg-status-danger/[0.08] text-status-danger [&>svg]:text-status-danger [&>[data-slot=alert-icon]]:text-status-danger',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

/**
 * @private
 */
const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props} />
))
Alert.displayName = 'Alert'

const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h5 ref={ref} className={cn('mb-1 font-inter text-p-500 tracking-normal', className)}>
      {props.children}
    </h5>
  ),
)
AlertTitle.displayName = 'AlertTitle'

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('font-inter text-p-400 tracking-normal', className)} {...props} />
))
AlertDescription.displayName = 'AlertDescription'

export { Alert, AlertTitle, AlertDescription }
